import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import { useMediaQuery } from 'react-responsive';

import { isIOS } from '../../../util/device-detector';
import getConfig from '../../../util/config';
import {
  EVENT_TYPE_LABELS,
  PURCHASE_TYPE,
  SEARCH_SUBSCRIPTION_LABELS,
  THANK_YOU_HEADERS,
} from './constants';
import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import { BASENAME } from '../../../constants/basenames';
import { PRO_REGISTRATION_PERSONAS } from '../../../views/auth/RegisterView/helpers';
import WaldoSpeechBubble from '../WaldoSpeechBubble';
import { getAsset } from '../../../util/assets';
import { breakpointDown } from '../../../services/responsive/responsive';
import Button from '../Button';

const propTypes = {
  purchaseType: PropTypes.oneOf(Object.keys(PURCHASE_TYPE)),
  purchaseMetadata: PropTypes.shape({
    eventAddonDetails: PropTypes.shape({
      eventAddonItemTypes: PropTypes.arrayOf(
        Object.keys(EVENT_ADDON_ITEM_TYPE),
      ),
      freeEventConfigured: PropTypes.bool,
    }),
    subscriptionDetails: PropTypes.shape({
      searchSubscriptionItem: PropTypes.shape({
        id: PropTypes.string,
      }),
      photoManagerSubscriptionItem: PropTypes.shape({
        id: PropTypes.string,
      }),
      freeTrialAvailable: PropTypes.bool,
    }),
    verticalDetails: PropTypes.shape({
      persona: PropTypes.oneOf(PRO_REGISTRATION_PERSONAS),
      basename: PropTypes.oneOf(Object.values(BASENAME)),
    }),
  }),
  onRequestClose: PropTypes.func,
};
const ThankYou = ({
  purchaseType = PURCHASE_TYPE.EVENTS,
  purchaseMetadata,
  onRequestClose,
}) => {
  const config = getConfig();
  const isMobile = useMediaQuery(breakpointDown('sm'));

  const appUrl = isIOS()
    ? config.mobile.iosAppUrl
    : config.mobile.androidAppUrl;

  const { eventAddonDetails, subscriptionDetails, verticalDetails } =
    purchaseMetadata ?? {};
  const {
    searchSubscriptionItem,
    photoManagerSubscriptionItem,
    freeTrialAvailable,
  } = subscriptionDetails ?? {};

  return (
    <div className="thank-you-container">
      <WaldoSpeechBubble position="top" className="mb-4">
        {THANK_YOU_HEADERS({ purchaseType, proRole: verticalDetails?.role })}
      </WaldoSpeechBubble>
      <div className="text-center">
        {eventAddonDetails && (
          <Fragment>
            <h3>You have successfully activated:</h3>
            {eventAddonDetails.eventAddonItemTypes.map(type => (
              <p key={type} className="mb-0">
                {EVENT_TYPE_LABELS[type]}
              </p>
            ))}
            <div className="mb-3" />
          </Fragment>
        )}
        {searchSubscriptionItem && (
          <Fragment>
            <h3>You have successfully activated:</h3>
            <p className="mb-3">
              {SEARCH_SUBSCRIPTION_LABELS[searchSubscriptionItem.plan]}{' '}
              Subscription
            </p>
          </Fragment>
        )}
        {photoManagerSubscriptionItem && (
          <Fragment>
            <h3>You have successfully activated:</h3>
            <p className="mb-3">
              Photo Manager Subscription
              {freeTrialAvailable ? ' (Free Trial)' : ''}
            </p>
          </Fragment>
        )}
      </div>
      {!photoManagerSubscriptionItem && (
        <div className="thank-you-download-container">
          <QRCode
            id="app-download-qr"
            value={appUrl}
            renderAs="canvas"
            className="d-none d-md-block mr-2"
            size={120}
          />
          <div className="d-flex flex-column align-items-start">
            <div className="waldo-logo-container">
              <img
                src={getAsset('/waldo-icon.svg')}
                className="waldo-logo"
                alt="Waldo Logo"
              />
            </div>
            <p className="thank-you-download-copy">
              Experience your event to the fullest in the Waldo mobile app.
              {!isMobile && <> Scan this QR to download now!</>}
            </p>
            <Button
              btnBlock
              href={appUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-md-none mt-1"
            >
              Download the Waldo app
            </Button>
          </div>
        </div>
      )}
      {onRequestClose && (
        <div className="text-center mt-2">
          <Button btnStyle="link" onClick={onRequestClose}>
            &larr; Continue on web
          </Button>
        </div>
      )}
    </div>
  );
};

ThankYou.propTypes = propTypes;

export default ThankYou;
